// Ported from packages/workflow-engine-upstream/ui/src/Progress.tsx at commit bebc382; unchanged.
// The progress card: always on screen, so it stays small. It says what the run is doing now, in plain
// words, and whether the run waits on the model or on the engineer; the phases before open on request
// (server/progress.ts).
import { useEffect, useState } from 'react';
import type { Phase, Progress } from './api';
import { clock, waitLine, YOURS } from './progress-text';

function Done({ p }: { p: Phase }) {
  const ms = (p.endedAt ?? p.startedAt) - p.startedAt;
  return (
    <div className="phase">
      <span className="mark">✓</span>
      <span>
        {p.title}
        {p.detail && <span className="note"> · {p.detail}</span>}
        {p.yours && <span className="note"> · your time, not counted</span>}
      </span>
      <span className="mono st">{clock(ms)}</span>
    </div>
  );
}

function Steps({ s }: { s: NonNullable<Progress['steps']> }) {
  return (
    <>
      {s.current && (
        <span>
          Step {s.current.n} of {s.total}: {s.current.title}
          {s.current.activity && <span className="note"> · {s.current.activity}</span>}
        </span>
      )}
      {s.ahead.length > 0 && <span className="note">Also working ahead on step{s.ahead.length > 1 ? 's' : ''} {s.ahead.join(', ')}</span>}
      <div className="meter" title={`${s.done} of ${s.total} steps done`}>
        <div style={{ width: `${(100 * s.done) / Math.max(1, s.total)}%` }} />
      </div>
      <span className="note">{s.done} of {s.total} steps done</span>
    </>
  );
}

export function ProgressCard({ p, receivedAt }: { p: Progress; receivedAt: number }) {
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (p.finished) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [p.finished]);
  // The server's clock, moved on by the time since the progress came.
  const now = p.now + (p.finished ? 0 : Date.now() - receivedAt);
  const cur = p.phases.at(-1)!;
  const before = p.phases.slice(0, -1);
  const wait = p.modelSince !== undefined && !cur.yours ? waitLine(now - p.modelSince) : null;
  const running = !p.finished && !cur.yours;
  const runMs = p.runMs + (running ? now - p.now : 0);
  const yoursMs = p.yoursMs + (cur.yours && !p.finished ? now - p.now : 0);
  return (
    <section className={`progress ${cur.yours ? 'yours' : p.finished ? 'end' : 'busy'}`} aria-live="polite">
      <div className="now">
        <span className="dot" aria-hidden>{p.finished ? '■' : cur.yours ? '⏸' : '●'}</span>
        <b className="grow">{cur.title}</b>
        {!p.finished && <span className="mono">{clock(now - cur.startedAt)}</span>}
      </div>
      <div className="sub">
        {cur.detail && <span className="note">{cur.detail}</span>}
        {cur.yours && YOURS[cur.kind] && <span>{YOURS[cur.kind]}</span>}
        {p.steps && <Steps s={p.steps} />}
        {wait && <span className={wait.slow ? 'warn-text' : 'note'}>{wait.text}</span>}
      </div>
      {open && before.length > 0 && <div className="trail">{before.map((x, i) => <Done key={i} p={x} />)}</div>}
      <div className="foot note">
        <span className="grow">
          Run time {clock(runMs)}
          {yoursMs > 0 && ` · waiting on you ${clock(yoursMs)}`}
          {open && p.planning && ` · planning model calls ${p.planning.calls} of ${p.planning.stopAt} (safety stop)`}
        </span>
        {before.length > 0 && (
          <button type="button" className="chip" onClick={() => setOpen(!open)}>
            {open ? 'Hide' : `What it did so far (${before.length})`}
          </button>
        )}
      </div>
    </section>
  );
}
